import {
	Alert,
	Heading,
	Stack,
	Text,
	ThemeColorVariable,
	ThemeFontSizeVariable,
	ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { PostLoginOnboardingStepsProps } from "./post-login-onboarding";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BankInformationForm } from "../my-profile/forms/bank-information-form/bank-information-form";
import {
	createSystemBankAccountRecord,
	ISystemBankAccount,
	SystemBankAccountsSchema,
} from "../../models/user";
import { useUserContext } from "../../hooks/use-user-context";
import { isNullOrWhiteSpace } from "../../utils/string-helper";
import { useUpdateUserQuery } from "../../hooks/use-update-user-query";
import { isEqual } from "lodash";

export const PostLoginOnboardningBankAccount = ({ setContinueButton }: PostLoginOnboardingStepsProps) => {
	const { t } = useTranslation();
	const { employeeSettings, systems, user } = useUserContext();
	const { saveUserAsync } = useUpdateUserQuery(SystemBankAccountsSchema);

	const [formIsValid, setFormIsValid] = useState<boolean>();
	const [bankAccount, setBankAccount] = useState<ISystemBankAccount>(() => {
		if (user && user.systemBankAccounts.length > 0) {
			return user.systemBankAccounts[0];
		}

		const system = systems[0];
		const employeeIdForSystem = employeeSettings.find((es) => es.system === system.name)?.employeeId;
		return createSystemBankAccountRecord(system, employeeIdForSystem);
	});

	const [haveInfoSaved] = useState(() => {
		const isEmpty = !user || user.systemBankAccounts.length === 0;

		return (
			!isEmpty &&
			(Boolean(user.systemBankAccounts[0].accountNumber) ||
				Boolean(user.systemBankAccounts[0].ibanAccountNumber))
		);
	});

	useEffect(() => {
		const save = async () => {
			let haveChanges = false;

			if (user && user.systemBankAccounts.length > 0) {
				haveChanges = !isEqual(bankAccount, user.systemBankAccounts[0]);
			} else {
				haveChanges =
					!isNullOrWhiteSpace(bankAccount.accountNumber) ||
					!isNullOrWhiteSpace(bankAccount.ibanAccountNumber);
			}

			if (haveChanges) {
				return await saveUserAsync({ systemBankAccounts: [bankAccount] });
			} else {
				return true;
			}
		};

		setContinueButton({
			isDisabled:
				!haveInfoSaved &&
				(!formIsValid ||
					isNullOrWhiteSpace(bankAccount.accountNumber) ||
					isNullOrWhiteSpace(bankAccount.accountNumber)),
			save: save,
		});
	}, [bankAccount, formIsValid, haveInfoSaved, saveUserAsync, setContinueButton, user]);

	return (
		<Stack gap={ThemeSpaceVariable.Large}>
			<Stack>
				<Heading as="h1">
					{!haveInfoSaved
						? t("post_login_onboarding_bank_account_title_empty")
						: t("post_login_onboarding_bank_account_title")}
				</Heading>
				<Text color={ThemeColorVariable.OnSurfaceSubdued} fontSize={ThemeFontSizeVariable.Large}>
					{t("post_login_onboarding_bank_account_info")}
				</Text>
			</Stack>
			<Stack>
				<BankInformationForm
					systemBankAccount={bankAccount}
					setSystemBankAccount={setBankAccount}
					setFormIsValid={setFormIsValid}
				/>
				{haveInfoSaved && (
					<Alert iconVerticalAlign="flex-start" type="neutral">
						{t("bank_alert_existing_information")}
					</Alert>
				)}
			</Stack>
		</Stack>
	);
};
